/**
 * Dropdown Component
 * Menu and dropdown components for actions and navigation
 */

import React from "react";
import { SpacingToken } from "../foundation";

export type DropdownSizeToken = "small" | "medium" | "large";
export type DropdownPlacementToken =
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "top"
  | "top-start"
  | "top-end"
  | "left"
  | "right";

export const dropdownSize = {
  small: (): DropdownSizeToken => "small",
  medium: (): DropdownSizeToken => "medium",
  large: (): DropdownSizeToken => "large",
} as const;

export const dropdownPlacement = {
  bottom: (): DropdownPlacementToken => "bottom",
  bottomStart: (): DropdownPlacementToken => "bottom-start",
  bottomEnd: (): DropdownPlacementToken => "bottom-end",
  top: (): DropdownPlacementToken => "top",
  topStart: (): DropdownPlacementToken => "top-start",
  topEnd: (): DropdownPlacementToken => "top-end",
  left: (): DropdownPlacementToken => "left",
  right: (): DropdownPlacementToken => "right",
} as const;

export interface DropdownItem {
  key: string;
  label: React.ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  description?: string;
  destructive?: boolean;
  separator?: boolean;
}

export interface DropdownProps {
  items: DropdownItem[];
  trigger: React.ReactNode;
  placement?: DropdownPlacementToken;
  size?: DropdownSizeToken;
  disabled?: boolean;
  closeOnSelect?: boolean;
  spacing?: SpacingToken;
  className?: string;
}

export interface MenuProps {
  children: React.ReactNode;
  className?: string;
}

export interface MenuButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export interface MenuListProps {
  children: React.ReactNode;
  className?: string;
}

export interface MenuItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  destructive?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

export interface MenuDividerProps {
  className?: string;
}

export function Dropdown({
  items,
  trigger,
  placement = dropdownPlacement.bottom(),
  size = dropdownSize.medium(),
  disabled = false,
  closeOnSelect = true,
  spacing,
  className,
}: DropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleItemClick = (item: DropdownItem) => {
    if (closeOnSelect) {
      setIsOpen(false);
    }
    item.onClick?.();
  };

  // Implementation will be added later
  return (
    <div className={className}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <div>
          {items.map((item) => (
            <React.Fragment key={item.key}>
              {item.separator && <hr />}
              <button onClick={() => handleItemClick(item)} disabled={disabled || item.disabled}>
                {item.icon}
                <div>
                  {item.label}
                  {item.description && <div>{item.description}</div>}
                </div>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function Menu({ children, className }: MenuProps) {
  return <div className={className}>{children}</div>;
}

function MenuButton({ children, onClick, className }: MenuButtonProps) {
  return (
    <button className={className} onClick={onClick}>
      {children}
    </button>
  );
}

function MenuList({ children, className }: MenuListProps) {
  return <div className={className}>{children}</div>;
}

function MenuItem({ children, onClick, href, disabled, destructive, icon, className }: MenuItemProps) {
  const Component = href ? "a" : "button";

  return (
    <Component className={className} onClick={onClick} href={href} disabled={disabled}>
      {icon}
      {children}
    </Component>
  );
}

function MenuDivider({ className }: MenuDividerProps) {
  return <hr className={className} />;
}

// Add compound component properties
(Dropdown as any).Menu = Menu;
(Dropdown as any).Button = MenuButton;
(Dropdown as any).List = MenuList;
(Dropdown as any).Item = MenuItem;
(Dropdown as any).Divider = MenuDivider;
